import { ReactNode } from 'react';
import { Badge } from '@safetyhub/ui';
import type { RiskAssessmentConfig } from '@safetyhub/shared';
import { ModifiedFieldInfo } from '../../../lib/inspections';
import { ArrowUturnLeftIcon } from '../../icons';

interface ModifiedFieldHighlightProps {
  /** 해당 필드의 수정 정보 */
  modification?: ModifiedFieldInfo;
  /** 감싸질 내용 */
  children: ReactNode;
  /** 추가 클래스 */
  className?: string;
  /** 원본 값 포맷터 (기본: 자동 포맷팅) */
  formatOriginal?: (value: unknown) => string;
  /** 인라인 원본 표시 여부 (기본: true) */
  showOriginalInline?: boolean;
  /**
   * 원본 표시 위치:
   * - 'block' (기본): children 아래 한 줄 차지 (부모 layout 높이 영향)
   * - 'tooltip': 원본 inline div 미렌더링, wrapping div의 title attribute로 표시 (hover 시 브라우저 tooltip)
   */
  originalPosition?: 'block' | 'tooltip';
  /** 편집 가능 여부 (true일 때만 되돌리기 버튼 활성화) */
  editable?: boolean;
  /** 되돌리기 콜백 */
  onRevert?: () => void | Promise<void>;
}

/**
 * AI 원본에서 수정된 필드를 하이라이트하고
 * 인라인으로 원본 값을 표시하는 컴포넌트
 */
export function ModifiedFieldHighlight({
  modification,
  children,
  className = '',
  formatOriginal,
  showOriginalInline = true,
  originalPosition = 'block',
  editable = false,
  onRevert,
}: ModifiedFieldHighlightProps) {
  // 수정되지 않았으면 그냥 children 반환
  if (!modification) {
    return <div className={className}>{children}</div>;
  }

  // 원본 값 포맷팅
  const formatValue = (value: unknown): string => {
    if (formatOriginal) {
      return formatOriginal(value);
    }

    // 기본 포맷터
    if (value === null || value === undefined) {
      return '(없음)';
    }
    if (typeof value === 'string') {
      return value || '(없음)';
    }
    if (typeof value === 'number') {
      return String(value);
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return '(없음)';
      return value.map(v => typeof v === 'string' ? v : JSON.stringify(v)).join(', ');
    }
    if (typeof value === 'object') {
      // riskAssessment의 경우 label 표시
      const obj = value as Record<string, unknown>;
      if ('label' in obj && 'value' in obj) {
        return `${obj.label} (${obj.value})`;
      }
      // location의 경우
      if ('area' in obj || 'detail' in obj) {
        return [obj.area, obj.detail].filter(Boolean).join(' - ') || '(없음)';
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  // 되돌리기 핸들러
  const handleRevert = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRevert) {
      await onRevert();
    }
  };

  const originalValueText = `원본: ${formatValue(modification.originalValue)}`;

  return (
    <div
      className={`relative ${className}`}
      title={originalPosition === 'tooltip' ? originalValueText : undefined}
    >
      {/* 하이라이트 배경 */}
      <div className="absolute inset-0 -m-0.5 bg-amber-50 border border-amber-200 rounded pointer-events-none" />

      {/* 되돌리기 아이콘 */}
      <div className="absolute -top-1.5 -right-1.5 z-10">
        {editable && onRevert ? (
          <button
            type="button"
            onClick={handleRevert}
            className="inline-flex items-center justify-center w-5 h-5 text-xs bg-amber-500 text-white rounded-full shadow-sm hover:bg-amber-600 transition-colors"
            title="원본으로 되돌리기"
            aria-label="원본으로 되돌리기"
          >
            <ArrowUturnLeftIcon className="h-3 w-3" />
          </button>
        ) : (
          <span className="inline-flex items-center justify-center w-4 h-4 bg-amber-500 text-white rounded-full shadow-sm">
            <ArrowUturnLeftIcon className="h-2.5 w-2.5" />
          </span>
        )}
      </div>

      {/* 내용 */}
      <div className="relative">
        {children}

        {/* 인라인 원본 표시 (block 모드일 때만, tooltip 모드는 wrapping div title로 대체) */}
        {showOriginalInline && originalPosition !== 'tooltip' && (
          <div className="mt-0.5 text-xs text-amber-600 flex items-center gap-1">
            <span className="text-amber-400">└</span>
            <span className="font-medium">원본:</span>
            <span className="text-amber-700">{formatValue(modification.originalValue)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * config.methodType에 부합하지 않는 modification 제거.
 * THREE_LEVEL: likelihood/severity modification 숨김 (UI에 입력 없음)
 * FREQUENCY_SEVERITY: riskLevel modification 숨김 (자동 산출)
 */
export function filterModificationsByConfig(
  modifiedFields: ModifiedFieldInfo[] | undefined,
  config: RiskAssessmentConfig | null | undefined,
): ModifiedFieldInfo[] {
  if (!modifiedFields) return [];
  if (!config) return modifiedFields;

  const isThreeLevel = config.methodType === 'THREE_LEVEL';
  return modifiedFields.filter((m) => {
    if (isThreeLevel) {
      return m.field !== 'riskAssessment.likelihood' && m.field !== 'riskAssessment.severity';
    }
    return m.field !== 'riskAssessment.riskLevel';
  });
}

/**
 * 수정 정보에서 특정 필드 찾기 헬퍼
 */
export function findModification(
  modifiedFields: ModifiedFieldInfo[] | undefined,
  field: string
): ModifiedFieldInfo | undefined {
  if (!modifiedFields) return undefined;
  return modifiedFields.find(m => m.field === field);
}

/**
 * 배열 필드의 추가/삭제 변경사항 계산
 */
export interface ArrayChangeItem<T = string> {
  value: T;
  status: 'unchanged' | 'added' | 'removed';
}

export function calculateArrayChanges<T>(
  original: T[] | undefined,
  current: T[] | undefined,
  compareFn?: (a: T, b: T) => boolean
): ArrayChangeItem<T>[] {
  const origArray = original || [];
  const currArray = current || [];

  const compare = compareFn || ((a, b) => JSON.stringify(a) === JSON.stringify(b));

  const result: ArrayChangeItem<T>[] = [];
  const usedCurrentIndices = new Set<number>();

  // 1. 원본 배열 순서대로 처리 (삭제된 항목은 원래 위치에 유지)
  for (const origItem of origArray) {
    const currentIndex = currArray.findIndex((c, idx) => !usedCurrentIndices.has(idx) && compare(c, origItem));
    if (currentIndex !== -1) {
      // 원본에도 있고 현재에도 있음 → unchanged
      usedCurrentIndices.add(currentIndex);
      result.push({
        value: origItem,
        status: 'unchanged',
      });
    } else {
      // 원본에만 있음 → removed (원래 위치에 유지)
      result.push({
        value: origItem,
        status: 'removed',
      });
    }
  }

  // 2. 현재 배열에만 있는 항목들 (추가됨) - 맨 뒤에 추가
  for (let i = 0; i < currArray.length; i++) {
    if (!usedCurrentIndices.has(i)) {
      result.push({
        value: currArray[i],
        status: 'added',
      });
    }
  }

  return result;
}

/**
 * 배열 변경 상태 뱃지 컴포넌트
 */
export function ChangeStatusBadge({ status }: { status: 'added' | 'removed' }) {
  if (status === 'added') {
    return (
      <Badge tone="emerald" size="sm" className="ml-2">
        +추가됨
      </Badge>
    );
  }

  return (
    <Badge tone="rose" size="sm" className="ml-2">
      삭제됨
    </Badge>
  );
}

export default ModifiedFieldHighlight;
